
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { workoutsAPI, workoutIntervalsAPI } from '../api';
import { t, useTranslation } from '../i18n';
import { format } from "date-fns";
import { useAuth } from "../contexts/AuthContext";
import { toast } from '../components/common/Notifier';
import { formatPace } from "../utils/formatPace";
import { formatDuration } from "../utils/formatDuration";
import { getTranslatedError } from "../utils/errorMessage";
import { RPE_EMOJI } from "../utils/rpe";
import { paceWithTolerance } from "../utils/formatWorkout";
import { formatTargetPace, formatTargetTime } from "../utils/formatTarget";


function RpeSelector({ value, onSave }) {
  return (
    <div className="flex gap-1">
      {RPE_EMOJI.map((emoji, i) => {
        const rpe = i + 1;
        return (
          <button
            key={rpe}
            onClick={() => onSave(rpe)}
            className={`text-xl w-8 h-8 rounded-full transition-transform ${
              value === rpe ? 'bg-blue-100 scale-110 ring-2 ring-blue-400' : 'hover:bg-gray-100'
            }`}
            title={`RPE ${rpe}`}
          >
            {emoji}
          </button>
        );
      })}
    </div>
  );
}

function IntervalRow({ interval, patchInterval, t }) {
  return (
    <tr className="border-b last:border-0">
      <td className="py-2 pr-2 text-gray-400">{interval.index + 1}</td>

      <td className="py-2 pr-2">
        <select
          value={interval.type}
          onChange={(e) => patchInterval(interval.id, { type: e.target.value })}
          className={`text-xs px-2 py-1 rounded-full border-0 font-medium ${
            interval.type === 'work'
              ? 'bg-orange-100 text-orange-700'
              : 'bg-blue-100 text-blue-700'
          } ${interval.is_type_edited ? 'ring-1 ring-blue-400' : ''}`}
        >
          <option value="steady">{t('workouts.steady')}</option>
          <option value="warmup">{t('workouts.warmup')}</option>
          <option value="work">{t('workouts.work')}</option>
          <option value="recovery">{t('workouts.recovery')}</option>
          <option value="cooldown">{t('workouts.cooldown')}</option>
        </select>
      </td>

      <td className="py-2 pr-2">
        <EditableField
          value={interval.corrected_distance_m ?? interval.distance_m}
          originalValue={interval.distance_m}
          isEdited={interval.is_distance_edited}
          onSave={(v) => patchInterval(interval.id, { corrected_distance_m: v })}
          formatValue={(v) => formatDistance(v)}
          parseInput={parseDistanceInput}
          inputType="text"
        />
      </td>

      <td className="py-2 pr-2">{formatDuration(interval.duration_sec)}</td>

      <td className="py-2 pr-2">
        <EditableField
          value={interval.avg_pace_min_per_km}
          originalValue={interval.original_avg_pace_min_per_km}
          isEdited={interval.is_pace_edited}
          onSave={(v) => patchInterval(interval.id, { avg_pace_min_per_km: v })}
          formatValue={(v) => formatPace(v)}
          parseInput={parsePaceInput}
          inputType="text"
          validate={(v) => (v >= 2 && v <= 30) || 'error.pace_out_of_range'}
        />
      </td>

      <td className="py-2 pr-2">{formatPace(interval.gap_min_per_km)}</td>

      <td className="py-2 pr-2">{interval.elevation_gain_m != null ? `${Math.round(interval.elevation_gain_m)} м` : '—'}</td>

            {/* 8. ЧСС ср. (HR avg)  */}
      <td className="py-2 pr-2">
        <EditableField
          value={interval.avg_heartrate}
          originalValue={interval.original_avg_heartrate}
          isEdited={interval.is_avg_hr_edited}
          onSave={(v) => patchInterval(interval.id, { avg_heartrate: v })}
          formatValue={(v) => (v != null ? Math.round(v) : '—')}
          parseInput={(s) => parseInt(s, 10)}
          inputType="number"
        />
      </td>

      <td className="py-2 pr-2">
        <EditableField
          value={interval.max_heartrate}
          originalValue={interval.original_max_heartrate}
          isEdited={interval.is_hr_edited}
          onSave={(v) => patchInterval(interval.id, { max_heartrate: v })}
          formatValue={(v) => (v != null ? Math.round(v) : '—')}
          parseInput={(s) => parseInt(s, 10)}
          inputType="number"
        />
      </td>


      <td className="py-2 pr-2">
        <EditableField
          value={interval.avg_cadence}
          originalValue={interval.avg_cadence_original}
          isEdited={interval.is_cadence_edited}
          onSave={(v) => patchInterval(interval.id, { avg_cadence: v })}
          formatValue={(v) => (v != null ? Math.round(v) : '—')}
          parseInput={(s) => parseInt(s, 10)}
          inputType="number"
        />
      </td>
      <td className="py-2 pr-2">
        {interval.stride_length_m != null ? `${interval.stride_length_m.toFixed(2)} м` : '—'}
      </td>
    </tr>
  );
}

export function computeAveragesFromRows(rows) {
  if (!rows.length) return null;

  // разворачиваем group -> плоский список "виртуальных интервалов" с уже агрегированными полями
  const flat = rows.map((row) =>
    row.merged
      ? {
          distance_m: row.totalDistance,
          duration_sec: row.totalDuration,
          gap_min_per_km: row.avgGap,
          elevation_gain_m: row.totalElevation,
          avg_heartrate: row.avgHr,
          max_heartrate: row.maxHr,
        }
      : {
          distance_m: row.interval.corrected_distance_m ?? row.interval.distance_m,
          duration_sec: row.interval.duration_sec,
          gap_min_per_km: row.interval.gap_min_per_km,
          elevation_gain_m: row.interval.elevation_gain_m,
          avg_heartrate: row.interval.avg_heartrate,
          max_heartrate: row.interval.max_heartrate,
        }
  );

  return computeAverages(flat);
}

export function computeSumsFromRows(rows) {
  if (!rows.length) return null;

  const flat = rows.map((row) =>
    row.merged
      ? { distance_m: row.totalDistance, duration_sec: row.totalDuration, elevation_gain_m: row.totalElevation }
      : {
          corrected_distance_m: row.interval.corrected_distance_m,
          distance_m: row.interval.distance_m,
          duration_sec: row.interval.duration_sec,
          elevation_gain_m: row.interval.elevation_gain_m,
        }
  );

  return computeSums(flat);
}

function formatDistance(value) {
  if (value === null || value === undefined) return '—';
  return Math.round(value) + ' м';
}

// Парсинг ввода (возвращает число метров или null)
function parseDistanceInput(str) {
  if (!str || typeof str !== 'string') return null;
  const trimmed = str.trim().replace(',', '.');
  if (!trimmed) return null;
  const num = parseFloat(trimmed);
  if (isNaN(num) || num < 0) return null;
  return Math.round(num); // возвращаем целые метры
}

function parsePaceInput(str) {
  // Если это уже число - возвращаем как есть
  if (typeof str === 'number' && !isNaN(str)) {
    return str;
  }

  // Если это не строка - возвращаем null
  if (!str || typeof str !== 'string') {
    return null;
  }

  const trimmed = str.trim();
  if (!trimmed) return null;

  if (trimmed.includes(':')) {
    const [m, s] = trimmed.split(':').map(Number);
    if (Number.isNaN(m) || Number.isNaN(s)) return null;
    return m + s / 60;
  }

  const num = parseFloat(trimmed.replace(',', '.'));
  return Number.isNaN(num) ? null : num;
}

function EditableComment({ value, onSave, placeholder }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value ?? '');
    const [saving, setSaving] = useState(false);

    const startEdit = () => {
      setDraft(value ?? '');
      setEditing(true);
    };

    const commit = async () => {
      if (draft === (value ?? '')) {
        setEditing(false);
        return;
      }
      setSaving(true);
      try {
        await onSave(draft);
        setEditing(false);
      } finally {
        setSaving(false);
      }
    };

    const cancel = () => {
      setDraft(value ?? '');
      setEditing(false);
    };

    if (editing) {
      return (
        <div className="space-y-2">
          <textarea
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            className="w-full text-sm border border-blue-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-400"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={commit}
              disabled={saving}
              className="text-xs px-3 py-1 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? '⏳' : '✓'} {t('common.save')}
            </button>
            <button
              type="button"
              onClick={cancel}
              className="text-xs px-3 py-1 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        onClick={startEdit}
        className="group flex items-start justify-between gap-2 cursor-pointer rounded-lg p-2 -m-2 hover:bg-gray-50"
      >
        <p className={`text-sm ${value ? 'text-gray-700' : 'text-gray-400 italic'} whitespace-pre-wrap`}>
          {value || placeholder}
        </p>
        <span className="text-gray-300 group-hover:text-gray-500 shrink-0">✎</span>
      </div>
    );
  }

function EditableField({
  value,
  originalValue,
  isEdited,
  onSave,
  formatValue,
  parseInput,
  inputType = 'text',
  validate
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const startEdit = () => {
    setDraft(formatValue(value));
    setEditing(true);
  };

  const commit = async () => {
    setEditing(false);
    const parsed = draft === '' ? null : parseInput(draft);

    if (parsed !== null && validate) {
      const result = validate(parsed);
      if (result !== true) {
        toast.warning(t(result));
        setDraft(formatValue(value));
        return;
      }
    }

    if (parsed === value) return;
    await onSave(parsed);
  };

  if (editing) {
    return (
      <input
        type={inputType}
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === 'Enter' && commit()}
        className="w-16 px-1 py-0.5 border border-blue-400 rounded text-sm"
      />
    );
  }

  return (
    <span
      onClick={startEdit}
      className={`cursor-pointer px-1 rounded hover:bg-gray-100 ${
        isEdited ? 'text-blue-600 font-medium' : ''
      }`}
      title={isEdited ? `Original: ${formatValue(originalValue) ?? '—'}` : undefined}
    >
      {formatValue(value) ?? '—'}
      {isEdited && <span className="text-xs ml-1">✎</span>}
    </span>
  );
}


const INTERVAL_TYPES = ['all',  'work', 'recovery', 'steady'];

export function computeSums(intervals) {
  if (!intervals.length) return null;

  const totalDistance = intervals.reduce(
    (sum, i) => sum + (i.corrected_distance_m ?? i.distance_m ?? 0),
    0
  );
  const totalDuration = intervals.reduce((sum, i) => sum + (i.duration_sec || 0), 0);

  const elevationEntries = intervals.filter((i) => i.elevation_gain_m != null);
  const totalElevation = elevationEntries.length
    ? elevationEntries.reduce((sum, i) => sum + i.elevation_gain_m, 0)
    : null;

  return {
    totalDistance,
    totalDuration,
    totalElevation,
  };
}

export function groupConsecutiveByType(intervals) {
  if (!intervals.length) return [];

  const groups = [];
  let current = [intervals[0]];

  for (let i = 1; i < intervals.length; i++) {
    if (intervals[i].type === current[current.length - 1].type) {
      current.push(intervals[i]);
    } else {
      groups.push(current);
      current = [intervals[i]];
    }
  }
  groups.push(current);

  return groups.map((group) => {
    if (group.length === 1) {
      return { merged: false, interval: group[0] };
    }

    const totalDistance = group.reduce(
      (sum, i) => sum + (i.corrected_distance_m ?? i.distance_m ?? 0),
      0
    );
    const totalDuration = group.reduce((sum, i) => sum + (i.duration_sec || 0), 0);
    const avgPace = totalDistance > 0 ? (totalDuration / 60) / (totalDistance / 1000) : null;

    const elevationEntries = group.filter((i) => i.elevation_gain_m != null);
    const totalElevation = elevationEntries.length
      ? elevationEntries.reduce((sum, i) => sum + i.elevation_gain_m, 0)
      : null;

    const gapEntries = group.filter((i) => i.gap_min_per_km != null && i.distance_m);
    const gapDistance = gapEntries.reduce((sum, i) => sum + i.distance_m, 0);
    const avgGap = gapDistance > 0
      ? gapEntries.reduce((sum, i) => sum + i.gap_min_per_km * i.distance_m, 0) / gapDistance
      : null;

    const hrAvgEntries = group.filter((i) => i.avg_heartrate != null);
    const avgHr = hrAvgEntries.length
      ? hrAvgEntries.reduce((sum, i) => sum + i.avg_heartrate, 0) / hrAvgEntries.length
      : null;
    const cadenceEntries = group.filter((i) => i.avg_cadence != null);
    const avgCadence = cadenceEntries.length
      ? cadenceEntries.reduce((sum, i) => sum + i.avg_cadence, 0) / cadenceEntries.length
      : null;
        // stride length группы — пересчитываем из totalDistance/totalDuration/avgCadence,
    // а не усредняем stride_length_m по лапам (иначе искажение как с pace)
    const avgStrideLength = avgCadence && totalDuration > 0
      ? totalDistance / ((avgCadence / 60) * totalDuration)
      : null;
    const hrMaxEntries = group.filter((i) => i.max_heartrate != null);
    const maxHr = hrMaxEntries.length
      ? Math.max(...hrMaxEntries.map((i) => i.max_heartrate))
      : null;

    return {
      merged: true,
      type: group[0].type,
      indexRange: `${group[0].index + 1}-${group[group.length - 1].index + 1}`,
      key: `merged-${group[0].id}-${group[group.length - 1].id}`,
      totalDistance,
      totalDuration,
      avgPace,
      avgGap,
      totalElevation,
      avgHr,
      maxHr,
      avgCadence,
      avgStrideLength,
    };
  });
}

export function computeAverages(intervals) {
  if (!intervals.length) return null;

  const totalDistance = intervals.reduce((sum, i) => sum + (i.distance_m || 0), 0);
  const totalDuration = intervals.reduce((sum, i) => sum + (i.duration_sec || 0), 0);

  const avgPace = totalDistance > 0 ? (totalDuration / 60) / (totalDistance / 1000) : null;

  const gapEntries = intervals.filter((i) => i.gap_min_per_km != null && i.distance_m);
  const gapDistance = gapEntries.reduce((sum, i) => sum + i.distance_m, 0);
  const avgGap = gapDistance > 0
    ? gapEntries.reduce((sum, i) => sum + i.gap_min_per_km * i.distance_m, 0) / gapDistance
    : null;

  const elevationEntries = intervals.filter((i) => i.elevation_gain_m != null);
  const avgElevation = elevationEntries.length
    ? elevationEntries.reduce((sum, i) => sum + i.elevation_gain_m, 0) / elevationEntries.length
    : null;

  const hrAvgEntries = intervals.filter((i) => i.avg_heartrate != null);
  const avgHr = hrAvgEntries.length
    ? hrAvgEntries.reduce((sum, i) => sum + i.avg_heartrate, 0) / hrAvgEntries.length
    : null;

  const hrMaxEntries = intervals.filter((i) => i.max_heartrate != null);
  const maxHr = hrMaxEntries.length
    ? Math.max(...hrMaxEntries.map((i) => i.max_heartrate))
    : null;

  // Каденс — взвешенное по времени среднее (частота во времени, не в расстоянии)
  const cadenceEntries = intervals.filter((i) => i.avg_cadence != null && i.duration_sec);
  const cadenceDuration = cadenceEntries.reduce((sum, i) => sum + i.duration_sec, 0);
  const avgCadence = cadenceDuration > 0
    ? cadenceEntries.reduce((sum, i) => sum + i.avg_cadence * i.duration_sec, 0) / cadenceDuration
    : null;

  // Длина шага — пересчитываем из totalDistance/totalDuration/avgCadence,
  // а не усредняем stride_length_m по лапам напрямую (та же логика, что для pace)
  const avgStrideLength = avgCadence && totalDuration > 0
    ? totalDistance / ((avgCadence / 60) * totalDuration)
    : null;

  return {
    avgDistance: totalDistance / intervals.length,
    avgPace,
    avgGap,
    avgElevation,
    avgHr,
    maxHr,
    avgCadence,
    avgStrideLength,
  };
}


function WorkoutComplianceCard({ compliance, t }) {
  if (!compliance) return null;

  const {
    completion_pct,
    execution_score,
    hr_control_score,
    overall_score,
    recovery_cost,
    verdict,
    tags,
  } = compliance;

  // Цвета для общего балла
  const getVerdictStyle = (v) => {
    switch (v) {
      case 'excellent':
        return {
          bg: 'bg-emerald-50',
          border: 'border-emerald-200',
          text: 'text-emerald-700',
          badge: 'bg-emerald-600 text-white',
          label: t('compliance.verdict_excellent') || 'Отлично',
        };
      case 'good':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          text: 'text-blue-700',
          badge: 'bg-blue-600 text-white',
          label: t('compliance.verdict_good') || 'Хорошо',
        };
      case 'acceptable':
        return {
          bg: 'bg-amber-50',
          border: 'border-amber-200',
          text: 'text-amber-700',
          badge: 'bg-amber-500 text-white',
          label: t('compliance.verdict_acceptable') || 'Удовлетворительно',
        };
      case 'poor':
      default:
        return {
          bg: 'bg-rose-50',
          border: 'border-rose-200',
          text: 'text-rose-700',
          badge: 'bg-rose-600 text-white',
          label: t('compliance.verdict_poor') || 'Срыв плана',
        };
    }
  };

  const vStyle = getVerdictStyle(verdict);


  return (
    <div className={`rounded-xl shadow border p-5 ${vStyle.bg} ${vStyle.border}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-baseline gap-1.5">
            <span className="text-3xl font-extrabold tracking-tight text-gray-900">
              {overall_score}%
            </span>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {t('compliance.overall_score') || 'Оценка плана'}
            </span>
          </div>
          <span className={`text-xs px-2.5 py-1 rounded-full font-semibold shadow-sm ${vStyle.badge}`}>
            {vStyle.label}
          </span>
        </div>

        {/* Теги диагностики / предупреждения */}
        {tags && tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {tags.map((tag) => (
              <span
                key={tag}
                className="text-xs px-2 py-0.5 rounded-md bg-white/80 border border-amber-200 text-amber-800 font-medium"
              >
                {t(`compliance.tags.${tag}`) || tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* 4 составляющих метрики */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        {/* 1. Completion */}
        <div className="bg-white/90 backdrop-blur rounded-lg p-3 shadow-sm border border-gray-100">
          <p className="text-xs text-gray-500 mb-0.5">{t('compliance.completion') || 'Объём / Репсы'}</p>
          <div className="flex items-baseline justify-between">
            <span className="text-lg font-bold text-gray-800">{completion_pct}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-1.5 mt-2 overflow-hidden">
            <div
              className={`h-full rounded-full ${completion_pct >= 90 ? 'bg-emerald-500' : 'bg-amber-500'}`}
              style={{ width: `${Math.min(completion_pct, 100)}%` }}
            />
          </div>
        </div>

        {/* 2. Execution */}
        <div className="bg-white/90 backdrop-blur rounded-lg p-3 shadow-sm border border-gray-100">
          <p className="text-xs text-gray-500 mb-0.5">{t('compliance.execution') || 'Точность темпа'}</p>
          <div className="flex items-baseline justify-between">
            <span className="text-lg font-bold text-gray-800">
              {execution_score != null ? `${execution_score}%` : '—'}
            </span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-1.5 mt-2 overflow-hidden">
            <div
              className={`h-full rounded-full ${execution_score >= 90 ? 'bg-emerald-500' : 'bg-blue-500'}`}
              style={{ width: `${execution_score || 0}%` }}
            />
          </div>
        </div>

        {/* 3. HR Control */}
        <div className="bg-white/90 backdrop-blur rounded-lg p-3 shadow-sm border border-gray-100">
          <p className="text-xs text-gray-500 mb-0.5">{t('compliance.hr_control') || 'Контроль пульса'}</p>
          <div className="flex items-baseline justify-between">
            <span className="text-lg font-bold text-gray-800">
              {hr_control_score != null ? `${hr_control_score}%` : '—'}
            </span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-1.5 mt-2 overflow-hidden">
            <div
              className={`h-full rounded-full ${hr_control_score >= 90 ? 'bg-emerald-500' : 'bg-purple-500'}`}
              style={{ width: `${hr_control_score || 0}%` }}
            />
          </div>
        </div>

        {/* 4. Recovery Cost (Нагрузка) */}
        <div className="bg-white/90 backdrop-blur rounded-lg p-3 shadow-sm border border-gray-100">
          <p className="text-xs text-gray-500 mb-0.5 flex items-center justify-between">
            <span>{t('compliance.recovery_cost') || 'Нагрузка (sRPE)'}</span>
            <span>⚡</span>
          </p>
          <div className="flex items-baseline gap-1">
            <span className="text-lg font-bold text-gray-800">{recovery_cost}</span>
            <span className="text-xs text-gray-400">pts</span>
          </div>
          <p className="text-[10px] text-gray-400 mt-1.5 truncate">
            {workout_rpe_hint(recovery_cost)}
          </p>
        </div>
      </div>
    </div>
  );
}

// Вспомогательная подсказка по уровню стресса
function workout_rpe_hint(cost) {
  if (cost < 150) return 'Низкий стресс (Recovery)';
  if (cost < 300) return 'Умеренная нагрузка';
  if (cost < 500) return 'Высокая нагрузка';
  return 'Предельная нагрузка';
}


export default function WorkoutDetail({ workoutId, onBack }) {
  const { t } = useTranslation();
  const { athlete } = useAuth();
  const [workout, setWorkout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [mergeMode, setMergeMode] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [navigating, setNavigating] = useState(false);

  // Текущий отображаемый id — может отличаться от исходного workoutId
  // после перехода на соседний день
  const [currentWorkoutId, setCurrentWorkoutId] = useState(workoutId);


  const filteredIntervals = useMemo(() => {
    if (!workout?.intervals) return [];
    return typeFilter === 'all'
      ? workout.intervals
      : workout.intervals.filter((i) => i.type === typeFilter);
  }, [workout, typeFilter]);

  const displayRows = useMemo(() => {
    if (!mergeMode) return null;

    // Группируем ВСЕ интервалы (не отфильтрованные)
    const grouped = groupConsecutiveByType(workout.intervals);

    // Фильтруем группы по типу
    if (typeFilter !== 'all') {
      return grouped.filter((row) => {
        if (row.merged) {
          return row.type === typeFilter;
        }
        return row.interval.type === typeFilter;
      });
    }

    return grouped;
  }, [workout, mergeMode, typeFilter]);

  const averages = useMemo(() => {
    if (mergeMode && displayRows) return computeAveragesFromRows(displayRows);
    return computeAverages(filteredIntervals);
  }, [filteredIntervals, displayRows, mergeMode]);

  const overallAverages = useMemo(() => {
    if (!workout?.intervals?.length) return null;
    return computeAverages(workout.intervals);
  }, [workout]);

  const sums = useMemo(() => {
    if (mergeMode && displayRows) return computeSumsFromRows(displayRows);
    return computeSums(filteredIntervals);
  }, [filteredIntervals, displayRows, mergeMode]);

  // Если родитель передал новый workoutId (открыли другую тренировку) — сбрасываемся на него
  useEffect(() => {
    setCurrentWorkoutId(workoutId);
  }, [workoutId]);

  const load = useCallback(async (id) => {
    setLoading(true);
    try {
      const { data } = await workoutsAPI.get(id);
      setWorkout(data);
    } catch (e) {
      toast.error(t('error.general'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load(currentWorkoutId);
  }, [currentWorkoutId, load]);

  const handleNavigateDay = async (direction) => {
    if (!workout) return;
    const target = new Date(workout.scheduled_date);
    target.setDate(target.getDate() + direction);
    const dateStr = format(target, 'yyyy-MM-dd');

    setNavigating(true);
    try {
      const { data } = await workoutsAPI.list({ scheduled_date: dateStr });
      const found = Array.isArray(data) ? data[0] : data?.results?.[0];
      if (found) {
        setCurrentWorkoutId(found.id);
      } else {
        toast.info(t('workouts.no_workout_this_day'));
      }
    } catch (e) {
      toast.error(t('error.general'));
    } finally {
      setNavigating(false);
    }
  };
        // Функция пересчета
      const recalculateWorkout = async () => {
    setRecalculating(true);
    try {
      await workoutsAPI.recalculate(currentWorkoutId);
      await load(currentWorkoutId);
      toast.success(t('workouts.recalculated'));
    } catch (e) {
      toast.error(t('error.general'));
      console.error('Recalculate error:', e);
    } finally {
      setRecalculating(false);
    }
  };

  const patchWorkout = async (payload) => {
    try {
      const { data } = await workoutsAPI.update(currentWorkoutId, payload);  // было: workoutId
      setWorkout((prev) => ({ ...prev, ...data }));
      toast.success(t('workouts.updated'));
    } catch (e) {
      toast.error(t('error.general'));
    }
  };


  // 3. Потом patchInterval
  const patchInterval = async (intervalId, payload) => {
    try {
      const response = await workoutIntervalsAPI.update(intervalId, payload);
      if (response.data.workout_metrics) {
        setWorkout((prev) => ({
          ...prev,
          intervals: prev.intervals.map((i) =>
            i.id === intervalId ? { ...i, ...response.data.interval } : i
          ),
          actual_distance_km: response.data.workout_metrics.actual_distance_km,
          actual_duration_sec: response.data.workout_metrics.actual_duration_sec,
          actual_avg_pace_min_per_km: response.data.workout_metrics.actual_avg_pace_min_per_km,
        }));
      } else {
        await load();
      }
      toast.success(t('workouts.interval_updated'));
 } catch (e) {
    const errorData = e.response?.data;
    let message = t('error.general');

    if (errorData) {
      if (errorData.detail) {
        message = getTranslatedError(errorData.detail, t);
      } else {
        const firstKey = Object.keys(errorData)[0];
        if (firstKey) {
          const errorMsg = Array.isArray(errorData[firstKey])
            ? errorData[firstKey][0]
            : errorData[firstKey];
          message = getTranslatedError(errorMsg, t);
        }
      }
    }

    toast.error(message);
  }
};

  if (loading || !workout) {
    return <p className="text-gray-400">{t('common.loading')}</p>;
  }

  return (
  <div className="max-w-4xl mx-auto space-y-2">

    <div className="grid grid-cols-3 items-center">
      {/* Левая колонка */}
      <div className="justify-self-start">
        <button onClick={onBack} className="text-sm text-blue-600 hover:text-blue-800 font-medium">
          ← {t('workouts.back_to_week')}
        </button>
      </div>

      {/* Центральная колонка — дата со стрелками (только навигация, ничего больше) */}
      <div className="flex items-center justify-center gap-2">
        <button
          onClick={() => handleNavigateDay(-1)}
          disabled={navigating}
          className="p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-50 transition-colors text-3xl leading-none"
          aria-label={t('workouts.prev_day')}
        >
          ‹
        </button>
        <h1 className="text-xl font-bold text-gray-800 whitespace-nowrap">
          {t(`${workout.workout_type}`)} — {format(new Date(workout.scheduled_date), 'dd.MM.yyyy, EEEE')}
        </h1>
        <button
          onClick={() => handleNavigateDay(1)}
          disabled={navigating}
          className="p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-800 disabled:opacity-50 transition-colors text-3xl leading-none"
          aria-label={t('workouts.next_day')}
        >
          ›
        </button>
      </div>

      {/* Правая колонка */}
      <div className="justify-self-end flex items-center gap-2">
        {/* Звездочка (избранное) */}
        <button
          onClick={() => patchWorkout({ is_favorite: !workout.is_favorite })}
          className={`p-1.5 rounded-full transition-colors ${
            workout.is_favorite
              ? 'text-red-500 hover:text-red-600'
              : 'text-gray-300 hover:text-gray-400'
          }`}
          title={workout.is_favorite ? t('workouts.unfavorite') : t('workouts.favorite')}
          aria-label={workout.is_favorite ? t('workouts.unfavorite') : t('workouts.favorite')}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            className="w-6 h-6"
            fill={workout.is_favorite ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
            />
          </svg>
        </button>

        {/* Кнопка "Связать со Strava" - появляется только если нет связи */}
        {!workout.strava_activity_id && (
          <button
            onClick={() => window.open('/settings/strava', '_blank')}
            className="text-xs px-3 py-1.5 rounded-full bg-orange-50 text-orange-600 hover:bg-orange-100 transition-colors flex items-center gap-1"
            title={t('workouts.connect_strava_hint')}
          >
            <img src="/strava.png" alt="Strava" className="w-4 h-4" />
            {t('workouts.connect_strava')}
          </button>
        )}

        {/* Кнопка "Пересчитать" */}
        <button
          onClick={recalculateWorkout}
          disabled={recalculating}
          className={`text-xs px-3 py-1.5 rounded-full transition-colors flex items-center gap-1 ${
            recalculating
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
          }`}
          title={t('workouts.recalculate_hint')}
        >
          <span>{recalculating ? '⏳' : '🔄'}</span>
          {recalculating ? t('common.loading') : t('workouts.recalculate')}
        </button>
      </div>

    </div>

          {/* 2. Оценка выполнения плана */}
      {workout.compliance && (
        <WorkoutComplianceCard compliance={workout.compliance} t={t} />
      )}

    {/* Сводка плана на день */}
    {(workout.distance_km != null || workout.target_pace_min_per_km != null || workout.reps != null) && (
      <div className="bg-white rounded-xl shadow p-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <p className="text-gray-400">{t('workouts.planned_distance')}</p>
          <p className="text-lg font-semibold">
            {workout.distance_km != null ? `${workout.distance_km} km` : '—'}
          </p>
        </div>
        <div>
          <p className="text-gray-400">{t('workouts.planned_pace')}</p>
          <p className="text-lg font-semibold">
            {workout.target_pace_min_per_km != null
              ? `${paceWithTolerance(formatPace(workout.target_pace_min_per_km))} /km`
              : workout.target
              ? `${formatTargetPace(workout.target)} /km (${formatTargetTime(workout.target)})`
              : '—'}
          </p>
        </div>
        {workout.reps != null && (
          <div>
            <p className="text-gray-400">{t('workouts.planned_reps')}</p>
            <p className="text-lg font-semibold">
              {workout.reps}{workout.rep_duration ? ` × ${workout.rep_duration}` : ''}
            </p>
          </div>
        )}
        {workout.hr_targets?.main && (
          <div>
            <p className="text-gray-400">{t('workouts.target_hr')}</p>
            <p className="text-lg font-semibold flex items-center gap-1">
              {workout.hr_targets.main.low}–{workout.hr_targets.main.high} bpm
              {workout.hr_on_target != null && (
                <span>{workout.hr_on_target ? '✅' : '⚠️'}</span>
              )}
            </p>
          </div>
        )}
        {workout.hr_targets?.easy && workout.hr_targets?.marathon_pace && (
          <>
            <div>
              <p className="text-gray-400">{t('workouts.target_hr_easy')}</p>
              <p className="text-lg font-semibold">
                {workout.hr_targets.easy.low}–{workout.hr_targets.easy.high} bpm
              </p>
            </div>
            <div>
              <p className="text-gray-400">{t('workouts.target_hr_mp')}</p>
              <p className="text-lg font-semibold">
                {workout.hr_targets.marathon_pace.low}–{workout.hr_targets.marathon_pace.high} bpm
              </p>
            </div>
          </>
        )}
      </div>
    )}

    {/* RPE и комментарий — отдельная секция, после хедера */}
    <div className="bg-white rounded-xl shadow p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <p className="text-gray-400 text-xs mb-1">{t('workouts.rpe')}</p>
        <RpeSelector
          value={workout.rpe}
          onSave={(rpe) => patchWorkout({ rpe })}
        />
      </div>
      <div>
        <p className="text-gray-400 text-xs mb-1">{t('workouts.comment')}</p>
        <EditableComment
          value={workout.comment}
          onSave={(comment) => patchWorkout({ comment })}
          placeholder={t('workouts.comment_placeholder')}
        />
      </div>
    </div>

      {/* Сводка тренировки */}
      <div className="bg-white rounded-xl shadow p-6 grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
        <div>
          <p className="text-gray-400">{t('workouts.distance')}</p>
          <p className="text-lg font-semibold">{workout.actual_distance_km} км</p>
        </div>
        <div>
          <p className="text-gray-400">{t('workouts.time')}</p>
          <p className="text-lg font-semibold">{formatDuration(workout.actual_duration_sec)}</p>
        </div>
        <div>
          <p className="text-gray-400">{t('workouts.pace')}</p>
          <p className="text-lg font-semibold">{formatPace(workout.actual_avg_pace_min_per_km)} /км</p>
        </div>

        <div>
          <p className="text-gray-400">{t('workouts.hr_avg')}</p>
          <p className="text-lg font-semibold">
            {overallAverages?.avgHr != null ? Math.round(overallAverages.avgHr) : '—'}
          </p>
        </div>

        <div>
          <p className="text-gray-400">{t('workouts.elevation')}</p>
          <p className="text-lg font-semibold">{Math.round(workout.actual_elevation_gain_m ?? 0)} м</p>
        </div>

        <div>
          <p className="text-gray-400">Strava</p>
          {workout.strava_activity_id ? (
            <a
              href={`https://www.strava.com/activities/${workout.strava_activity_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-semibold text-orange-600 hover:text-orange-800 inline-flex items-center gap-1"
            >
              <img src="/strava.png" alt="Strava" className="w-4 h-4" />
              {t('workouts.view_on_strava')}
            </a>
          ) : (
            <p className="text-lg font-semibold text-gray-300">—</p>
          )}
        </div>
          </div>

      {/* Таблица лапов */}
      <div className="bg-white rounded-xl shadow p-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-800">{t('workouts.intervals')}</h2>

            <div className="flex gap-1 items-center">
              <button
                onClick={() => setMergeMode((prev) => !prev)}
                className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors mr-2 ${
                  mergeMode
                    ? 'bg-purple-600 text-white'
                    : 'bg-purple-50 text-purple-600 hover:bg-purple-100'
                }`}
                title={t('workouts.intervals_plus_hint')}
              >
                Intervals++
              </button>

              {INTERVAL_TYPES.map((type) => (
                <button
                  key={type}
                  onClick={() => setTypeFilter(type)}
                  className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${
                    typeFilter === type
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {type === 'all' ? t('workouts.all_types') : t(`workouts.${type}`)}
                </button>
              ))}
            </div>
          </div>


        {filteredIntervals.length ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 border-b">
                <th className="py-2 pr-2">#</th>
                <th className="py-2 pr-2">{t('workouts.type')}</th>
                <th className="py-2 pr-2">{t('workouts.distance')}</th>
                <th className="py-2 pr-2">{t('workouts.time')}</th>
                <th className="py-2 pr-2">{t('workouts.pace')}</th>
                <th className="py-2 pr-2">GAP</th>
                <th className="py-2 pr-2">{t('workouts.elevation')}</th>
                <th className="py-2 pr-2">{t('workouts.hr_avg')}</th>
                <th className="py-2 pr-2">{t('workouts.hr_max')}</th>
                <th className="py-2 pr-2">{t('workouts.cadence')}</th>
                <th className="py-2 pr-2">{t('workouts.stride')}</th>

              </tr>
            </thead>
            <tbody>
              {mergeMode
                ? displayRows.map((row) =>
                    row.merged ? (
                      <tr key={row.key} className="border-b last:border-0 bg-purple-50/40">
                        <td className="py-2 pr-2 text-gray-400">{row.indexRange}</td>
                        <td className="py-2 pr-2">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                            row.type === 'work' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {t(`workouts.${row.type}`)}
                          </span>
                        </td>
                        <td className="py-2 pr-2">{formatDistance(row.totalDistance)}</td>
                        <td className="py-2 pr-2">{formatDuration(row.totalDuration)}</td>
                        <td className="py-2 pr-2">{formatPace(row.avgPace)}</td>
                        <td className="py-2 pr-2">{formatPace(row.avgGap)}</td>
                        <td className="py-2 pr-2">{row.totalElevation != null ? `${Math.round(row.totalElevation)} м` : '—'}</td>
                        <td className="py-2 pr-2">{row.avgHr != null ? Math.round(row.avgHr) : '—'}</td>
                        <td className="py-2 pr-2">{row.maxHr != null ? Math.round(row.maxHr) : '—'}</td>
                        <td className="py-2 pr-2">{row.avgCadence != null ? Math.round(row.avgCadence) : '—'}</td>
                        <td className="py-2 pr-2">{row.avgStrideLength != null ? `${row.avgStrideLength.toFixed(2)} м` : '—'}</td>

                      </tr>
                    ) : (
                      <IntervalRow key={row.interval.id} interval={row.interval} patchInterval={patchInterval} t={t} />
                    )
                  )
                : filteredIntervals.map((interval) => (
                    <IntervalRow key={interval.id} interval={interval} patchInterval={patchInterval} t={t} />
                  ))}
            </tbody>
            {averages && (
              <tfoot>
                <tr className="border-t-2 font-semibold text-gray-700 bg-gray-50">
                  <td className="py-2 pr-2"></td>
                  <td className="py-2 pr-2 text-xs text-gray-500">{t('workouts.average')}</td>
                  <td className="py-2 pr-2">{Math.round(averages.avgDistance)} м</td>
                  <td className="py-2 pr-2">—</td>
                  <td className="py-2 pr-2">{formatPace(averages.avgPace)}</td>
                  <td className="py-2 pr-2">{formatPace(averages.avgGap)}</td>
                  <td className="py-2 pr-2">{averages.avgElevation != null ? `${Math.round(averages.avgElevation)} м` : '—'}</td>
                  <td className="py-2 pr-2">{averages.avgHr != null ? Math.round(averages.avgHr) : '—'}</td>  {/* HR avg */}
                  <td className="py-2 pr-2">{averages.maxHr != null ? Math.round(averages.maxHr) : '—'}</td>   {/* HR max */}
                  <td className="py-2 pr-2">{averages.avgCadence != null ? Math.round(averages.avgCadence) : '—'}</td>
                  <td className="py-2 pr-2">{averages.avgStrideLength != null ? averages.avgStrideLength.toFixed(2) + ' м' : '—'}</td>
                </tr>
                {sums && (
                <tr className="font-semibold text-gray-700 bg-gray-50">
                      <td className="py-2 pr-2"></td>
                      <td className="py-2 pr-2 text-xs text-gray-500">{t('workouts.sum')}</td>
                      <td className="py-2 pr-2">{formatDistance(sums.totalDistance)}</td>
                      <td className="py-2 pr-2">{formatDuration(sums.totalDuration)}</td>
                      <td className="py-2 pr-2">—</td>
                      <td className="py-2 pr-2">—</td>
                      <td className="py-2 pr-2">{sums.totalElevation != null ? `${Math.round(sums.totalElevation)} м` : '—'}</td>
                      <td className="py-2 pr-2">—</td>  {/* HR avg */}
                      <td className="py-2 pr-2">—</td>  {/* HR max */}
                      <td className="py-2 pr-2">—</td>
                      <td className="py-2 pr-2">—</td>
                    </tr>
                )}
              </tfoot>
            )}
          </table>
        ) : (
          <p className="text-sm text-gray-400">{t('workouts.no_intervals')}</p>
        )}

        <p className="text-xs text-gray-400 mt-3">
          ✎ {t('workouts.edit_hint')}
        </p>
      </div>
    </div>
  );
}